import { getDaysArray } from 'utils/date';
import { getAvailableFrameStartFromCellClick } from 'utils/booking';
import { useEffect } from 'react';
import { addDays, format } from 'date-fns';
import { Tooltip } from 'react-tooltip';
import { BookingSlot } from '../../api-types/models/Booking';
import { BookingTableCell } from '../bookingTableCell';
import { useAppSelector } from '../../app/hooks';
import { selectWeekType } from '../../app/bookingSlice';

import './index.css';

interface IProps {
    startOfWeekDate: Date;
    setSelectedDate: (date: Date) => void;
    slotsPerWeek: BookingSlot[];
    setSelectedSlot: (slot: BookingSlot) => void;
}

export function BookingTable({
    startOfWeekDate,
    setSelectedDate,
    slotsPerWeek,
    setSelectedSlot
}: Readonly<IProps>) {
    const weekType = useAppSelector(selectWeekType);

    useEffect(() => {
        const table = document.getElementById(
            'booking-table'
        ) as HTMLTableElement;

        // Attach a click event listener to the table.
        // Use closest('td') so the correct cell is found regardless of which
        // child element was the actual event.target (fixes Firefox where
        // clicking the empty area of a <td> targets the <td> itself rather
        // than the inner .wrapper div, breaking the old parentElement logic).
        const handleClick = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            const cell = target.closest('td');

            if (!cell) return;

            // If the click originated from inside a slot button, let the
            // slot's own onClick handler open the edit modal – don't open
            // the create modal on top of it.
            if (target.closest('.booking-content')) return;

            // Open the create-slot modal for:
            //  • empty cells (no slot at all)
            //  • active cells but click landed on empty space above/below a slot
            if (
                cell.classList.contains('empty') ||
                cell.classList.contains('active')
            ) {
                const rowIndex = (cell.parentElement as HTMLTableRowElement)
                    .rowIndex;
                const cellIndex = cell.cellIndex;

                if (cellIndex && rowIndex) {
                    const dayIndex = cellIndex - 1;
                    const hour = rowIndex + 5;
                    const { top, height } = cell.getBoundingClientRect();
                    const clickOffsetY = event.clientY - top;
                    setSelectedDate(
                        getAvailableFrameStartFromCellClick(
                            startOfWeekDate,
                            dayIndex,
                            hour,
                            clickOffsetY,
                            height,
                            slotsPerWeek
                        )
                    );
                }
            }
        };

        // Track hover state on active cells: highlight only when mouse is over
        // empty space (not over the .booking-content button). Use 'hover-empty'
        // class to conditionally apply the highlight.
        const handleMouseMove = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            const cell = target.closest('td');

            if (!cell) return;

            if (cell.classList.contains('active')) {
                const isOverSlot = target.closest('.booking-content');
                if (isOverSlot) {
                    cell.classList.remove('hover-empty');
                } else {
                    cell.classList.add('hover-empty');
                }
            }
        };

        const handleMouseLeave = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            const cell = target.closest('td');
            if (cell) {
                cell.classList.remove('hover-empty');
            }
        };

        table.addEventListener('click', handleClick);
        table.addEventListener('mousemove', handleMouseMove);
        table.addEventListener('mouseleave', handleMouseLeave, true);

        return () => {
            table.removeEventListener('click', handleClick);
            table.removeEventListener('mousemove', handleMouseMove);
            table.removeEventListener('mouseleave', handleMouseLeave, true);
        };
    }, [startOfWeekDate, setSelectedDate, slotsPerWeek]);

    return (
        <>
            <table className='booking-table' id='booking-table'>
                <thead>
                    <tr>
                        <th></th>
                        {getDaysArray(weekType).map((day, index) => (
                            <th
                                key={day}
                                data-for='tooltip-date'
                                data-tip={format(
                                    addDays(startOfWeekDate, index),
                                    'PPP'
                                )}
                            >
                                {day}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {Array.from({ length: 15 }, (_, index) => 6 + index).map(
                        (hour) => (
                            <tr key={hour}>
                                <th>{hour}</th>
                                {getDaysArray(weekType).map((day, index) => (
                                    <BookingTableCell
                                        key={`${day}-${hour}`}
                                        hour={hour}
                                        index={index}
                                        startOfWeekDate={startOfWeekDate}
                                        slotsPerWeek={slotsPerWeek}
                                        setSelectedSlot={setSelectedSlot}
                                    />
                                ))}
                            </tr>
                        )
                    )}
                </tbody>
            </table>
            <Tooltip id='tooltip-date' />
        </>
    );
}
